import React, { useState } from "react";

import EmptyState from "../../components/EmptyState";
import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Async, Kpi, KpiRow, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Opportunities (§4, tab 8 of 8).
 *
 * PER WORKSTATION-OPPORTUNITIES-APPROVED (docs/DECISION_LOG.md) AND
 * `design/system/audits/workstation-tabs.md` §C1
 * ====================================================================
 * `workstation.md` defines this tab as "Accumulated historical learnings +
 * noteworthy opportunities/problems Cipher surfaces." The audit found this
 * tab was wrongly marked `NONE` in the original build brief — it is not. Two
 * real, live, offer-scoped endpoints back exactly the "opportunities/problems"
 * half:
 *
 *   GET /api/performance/campaigns/scale-candidates?offer_id=  performance.py:1444
 *   GET /api/performance/campaigns/at-risk?offer_id=           performance.py:1419
 *
 * Both read `campaign_health_status`, `{ campaigns: [...], count }`. Each
 * campaign document carries a `recommendation` + `recommendation_detail`
 * already composed server-side (`services/campaign_health/health_monitor.py`)
 * — this tab renders that, it does not re-derive or restate it.
 *
 * Selecting a flagged campaign fetches its failure-mode diagnosis on demand
 * from GET /api/performance/campaigns/diagnosis/{campaign_id}
 * (performance.py:1469) — real, not fabricated, not pre-fetched for every row
 * (that would be an unrequested N+1 against a per-campaign endpoint).
 *
 * WHY THIS TAB WILL OFTEN BE EMPTY, AND WHY THAT IS CORRECT
 * ===========================================================
 * `campaign_health_status` is written only for a campaign the sweep actually
 * flagged, so on most offers both lists will legitimately be empty — not a
 * bug, not a denied request. `Async`'s `empty` prop renders that state
 * honestly, distinct from the loading and 403/error states it already handles.
 *
 * BUT EMPTY ALONE DOES NOT SAY WHICH KIND OF EMPTY (rule 10)
 * ==========================================================
 * CORRECTED 2026-09-09 (OFFER-PERFORMANCE-DEPARTMENT). This file used to say
 * "no scheduler job calls it, so an empty list is the expected first-load
 * state." That was true when written: the sweep had run once, on 2026-05-04,
 * and nothing scheduled it. It is no longer true — `campaign_health_sweep`
 * runs daily at 06:00 UTC.
 *
 * That makes the honest reading of an empty list depend on something the list
 * itself cannot show. "Nothing is at risk" and "nobody has looked" and "the
 * sweep crashed" all produce zero rows. So both list responses carry a `sweep`
 * record, and `SweepNote` below turns it into the sentence that says WHICH of
 * the three this is. An empty list is never described here without it.
 *
 * A zero on this screen is frequently the correct answer — no campaign
 * currently deteriorating is a good day, not a broken pipeline — and the point
 * of the sweep line is to let the Founder trust that zero rather than wonder
 * about it.
 *
 * LEARNINGS IS DELIBERATELY NOT ON THIS TAB
 * ==========================================
 * §64 of `workstation.md` names this a combined "Learnings · Opportunities"
 * tab. The audit (§C1, "Weaker, offer-adjacent candidates") and this
 * session's own check of `backend/routers/` confirm the one real endpoint
 * for historical learnings (`/api/lessons`) is not offer-filterable — its
 * filters are `status`/`business_unit`/`decision_type`/`significant`, no
 * `offer_id` — including it here would fake an offer scope it does not
 * have. `WORKSTATION-OPPORTUNITIES-APPROVED` scopes
 * the approval narrowly to Scale Candidate / At Risk campaigns, so that is
 * what ships. Learnings is left for a separate, explicitly-approved task
 * rather than half-built alongside this one.
 */

/** One flagged campaign row. Renders exactly what the endpoint sent —
 *  no client-side re-derivation of the flag or the recommendation. */
function CampaignRow({ campaign, metrics, onSelect, selected }) {
  const id = campaign.campaign_id;
  return (
    <li className="ws-understanding-item">
      <button
        type="button"
        className="ws-session-item"
        aria-pressed={selected}
        onClick={() => onSelect(id)}
        style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}
      >
        <span style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: 12 }}>
          <span>{campaign.campaign_name || id}</span>
          {/* A MECHANICAL FLAG IS NOT A VERDICT (Founder, 2026-09-10).
              These rows come from pure arithmetic over daily figures — no
              model, no judgement. One of them read "Scale — ready to scale"
              on a campaign that had spent $4,441.85 for $0.00 and zero
              conversions. So an unassessed row says it is unassessed, and the
              only verdict word that ever appears is one a Specialist
              actually reached. */}
          {campaign.assessed === true && campaign.assessed_decision ? (
            <span className="ws-tag" data-testid="ws-row-assessed">
              {campaign.assessed_decision}
            </span>
          ) : (
            <span className="ws-tag ws-tag-muted" data-testid="ws-row-unassessed">
              {campaign.assessed === null
                ? "Assessment unavailable"
                : "Not yet assessed"}
            </span>
          )}
        </span>
        {campaign.recommendation_detail ? (
          <span className="dim" style={{ fontSize: "var(--f-sm, 12px)" }}>
            {campaign.recommendation_detail}
          </span>
        ) : null}
        {campaign.assessed === false ? (
          <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
            Flagged automatically. Select it to see whether Cipher has formed a
            view.
          </span>
        ) : null}
        {metrics ? (
          <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
            {metrics}
          </span>
        ) : null}
        {/* When THIS row was flagged. The sweep line above says when the last
            sweep ran; a row older than that survived from an earlier sweep,
            and without this date the two are indistinguishable. */}
        {when(campaign.analyzed_at) ? (
          <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
            Flagged {when(campaign.analyzed_at)}
          </span>
        ) : null}
      </button>
    </li>
  );
}

function scaleMetrics(c) {
  const parts = [];
  if (c.latest_cpa !== undefined) parts.push(`CPA $${Number(c.latest_cpa).toFixed(2)}`);
  if (c.latest_ctr !== undefined) parts.push(`CTR ${Number(c.latest_ctr).toFixed(2)}%`);
  if (c.consecutive_improvement_days !== undefined) {
    parts.push(`${c.consecutive_improvement_days} consecutive good days`);
  }
  return parts.join(" · ");
}

function atRiskMetrics(c) {
  const parts = [];
  if (c.latest_cpa !== undefined) parts.push(`CPA $${Number(c.latest_cpa).toFixed(2)}`);
  if (c.latest_cpc !== undefined) parts.push(`CPC $${Number(c.latest_cpc).toFixed(2)}`);
  if (c.consecutive_deterioration_days !== undefined) {
    parts.push(`${c.consecutive_deterioration_days} consecutive bad days`);
  }
  return parts.join(" · ");
}

/**
 * Internal constants, as an operator reads them.
 *
 * `campaign_health_status.status` and the diagnosis `failure_mode` are
 * SHOUTED_CONSTANTS chosen for code, and both were being printed to the screen
 * verbatim. An operator should never have to learn that BACKEND_FAILING means
 * the traffic is fine and the page is not converting. Anything absent from
 * these maps falls back to a readable sentence-case version rather than the
 * raw token.
 */
const STATUS_WORDS = {
  SCALE_CANDIDATE: "Worth scaling",
  AT_RISK: "At risk",
  SCALE: "Scale",
  REDUCE: "Reduce",
  STOP: "Stop",
  MONITOR: "Monitor",
  HOLD: "Hold",
};

const FAILURE_WORDS = {
  CREATIVE_FAILING: "The ads are not earning attention",
  BACKEND_FAILING: "The ads work, but the page is not converting",
  HEALTHY: "Nothing is wrong with this campaign",
  UNDERPERFORMING: "Results are below where they should be",
  // NOT a fifth kind of failure. It is the explicit absence of a verdict,
  // added with issue #165 so that "we could not measure this" stops being
  // spelled the same way as "the creative is failing". The reason is always
  // in `signals`, which the panel already renders underneath.
  UNDIAGNOSED: "Not enough evidence to judge this campaign",
};

/** Any internal token, made readable. Never shown raw. */
function readable(token, map) {
  if (!token) return null;
  const key = String(token).toUpperCase();
  if (map[key]) return map[key];
  const words = String(token).replace(/_/g, " ").toLowerCase().trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null;
}

/** A timestamp as something a person reads, or null if it is not one. */
function when(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * The sentence that says which kind of empty this is.
 *
 * Four inputs, four different sentences — collapsing any two of them back
 * together is the thing this function exists to prevent:
 *
 *   sweep missing    the server predates this field; say so rather than
 *                    inventing a reassuring answer
 *   ran === null     the sweep lookup itself failed
 *   ran === false    no sweep has ever completed
 *   status failed    the last sweep ran and crashed; rows below are stale
 *   status ok        a real check, with its time and scope
 */
function sweepSentence(sweep) {
  if (!sweep) {
    return {
      tone: "unknown",
      text: "This server did not report when the health sweep last ran, so an empty list below cannot be read as an all-clear.",
    };
  }
  if (sweep.ran === null) {
    return {
      tone: "error",
      text: `Could not read when the health sweep last ran (${sweep.error || "unknown error"}). An empty list below is not an all-clear.`,
    };
  }
  if (sweep.ran === false) {
    return {
      tone: "never",
      text: "The health sweep has never completed, so nothing has been checked yet. An empty list below means nobody has looked — not that nothing is wrong.",
    };
  }
  if (sweep.status === "failed") {
    return {
      tone: "error",
      text: `The last health sweep failed${when(sweep.swept_at) ? ` on ${when(sweep.swept_at)}` : ""}${sweep.error ? ` (${sweep.error})` : ""}. Anything below is from an earlier sweep and may be out of date.`,
    };
  }
  const scanned =
    typeof sweep.campaigns_scanned === "number"
      ? `, ${sweep.campaigns_scanned} campaign${sweep.campaigns_scanned === 1 ? "" : "s"} scanned`
      : "";
  const how = sweep.trigger === "founder" ? " (run by hand)" : "";
  return {
    tone: "ok",
    text: `Last checked ${when(sweep.swept_at) || "at an unrecorded time"}${how}${scanned}.`,
  };
}

/** The sweep line above both lists. */
function SweepNote({ sweep }) {
  const { tone, text } = sweepSentence(sweep);
  return (
    <div
      className="state ws-state"
      role="status"
      style={{ marginBottom: 12, opacity: tone === "ok" ? 0.75 : 1 }}
    >
      {tone !== "ok" ? <strong>Heads up: </strong> : null}
      {text}
    </div>
  );
}

/** How an empty list should describe itself, given the sweep behind it. */
function emptyFor(label, sweep) {
  const { tone } = sweepSentence(sweep);
  if (tone === "ok") {
    const at = when(sweep.swept_at);
    return `Nothing flagged ${label} for this offer as of the last sweep${at ? ` (${at})` : ""}. That is a real result, not a missing one.`;
  }
  if (tone === "never") {
    return `Nothing flagged ${label} — but the health sweep has never completed, so this offer has not actually been checked.`;
  }
  if (tone === "error") {
    return `Nothing flagged ${label} — but the last sweep did not complete, so this list cannot be read as an all-clear.`;
  }
  return `Nothing flagged ${label} for this offer. This server did not report when the sweep last ran, so this is not a confirmed all-clear.`;
}

/** One of the two flagged-campaign lists. `label` names what this list flags
 *  ("as a Scale Candidate", "At Risk"); the empty wording is derived from the
 *  sweep record in the same response, never hard-coded, so it can never claim
 *  an all-clear the sweep does not support. */
function FlaggedList({ result, label, metricsFn, selectedId, onSelect }) {
  // `Async`'s own `empty` prop cannot see the response body, so it gets the
  // sweep-free wording; the branch below, which can, replaces it with the
  // wording the actual sweep state justifies.
  const fallback = emptyFor(label, undefined);
  return (
    <Async result={result} empty={fallback}>
      {(data) => {
        const rows = Array.isArray(data) ? data : data?.campaigns || [];
        if (rows.length === 0) {
          return (
            <div className="state ws-state">
              {emptyFor(label, Array.isArray(data) ? undefined : data?.sweep)}
            </div>
          );
        }
        return (
          <ul className="ws-list">
            {rows.map((c, i) => (
              <CampaignRow
                key={c.campaign_id || i}
                campaign={c}
                metrics={metricsFn(c)}
                selected={selectedId === c.campaign_id}
                onSelect={onSelect}
              />
            ))}
          </ul>
        );
      }}
    </Async>
  );
}

/** The diagnosis panel body — nothing selected, loading, denied/error, or
 *  the real `failure_mode` + signals `diagnose_campaign` composed. */
function DiagnosisBody({ campaignId, diagnosis }) {
  if (!campaignId) {
    return (
      <EmptyState
        title="No campaign selected"
        detail="Select a Scale Candidate or At Risk campaign to see its failure-mode diagnosis."
      />
    );
  }
  return (
    <Async
      result={diagnosis}
      empty="No diagnosis available for this campaign yet."
      deniedLabel="Diagnosis requires a signed-in operator, and the server refused it. That is the gate working, not a fault."
    >
      {(data) => (
        <div>
          <p className="ws-understanding-lead" style={{ marginTop: 0 }}>
            {readable(data.failure_mode, FAILURE_WORDS)}
            {data.recommended_action ? ` — ${data.recommended_action}` : ""}
          </p>
          {Array.isArray(data.signals) && data.signals.length > 0 ? (
            <ul className="ws-list">
              {data.signals.map((s, i) => (
                <li key={i} className="ws-understanding-item">
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {data.metrics ? (
            <KpiRow>
              <Kpi label="Avg CPA" value={data.metrics.avg_cpa} format="currency" />
              <Kpi label="Avg ROAS" value={data.metrics.avg_roas} format="ratio" />
              <Kpi label="Avg CVR" value={data.metrics.avg_cvr} format="percent" />
              <Kpi label="Days analyzed" value={data.metrics.days_analyzed} />
            </KpiRow>
          ) : null}
        </div>
      )}
    </Async>
  );
}

/**
 * The operator-facing brief for one flagged campaign.
 *
 * LEADS WITH THE DECISION, AND SHOWS NOTHING THE OPERATOR CANNOT ACT ON
 * =====================================================================
 * Everything rendered here arrives as a named field from
 * `GET /performance/campaigns/{id}/assessment`, which serves a stored,
 * validated `decision_briefs` row through an allowlist. This component does no
 * cleaning, no parsing and no truncation of model prose — if it had to, the
 * contract upstream would be broken. The Decision's raw verbatim `conclusion`
 * is structurally unable to reach this file.
 *
 * SHORT BY DEFAULT BECAUSE THE CONTENT IS BOUNDED, NOT BECAUSE IT IS CLIPPED
 * =========================================================================
 * `contract.validate` caps assessment, action and risk lengths and allows at
 * most three signals, so the default card is short by construction. There is
 * deliberately NO internal scroll: a scrollbar inside a card hides content and
 * makes the operator hunt for it. The card grows only when someone opens
 * "View supporting data", which is their choice.
 */
function ConfidenceChip({ value }) {
  return (
    <span className="ws-tag" title="How much weight to put on this recommendation">
      Confidence: {value}
    </span>
  );
}

function SupportingData({ data }) {
  const [open, setOpen] = React.useState(false);
  const rows = Object.entries(data || {});
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        className="ws-linkbtn"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="ws-brief-supporting-toggle"
      >
        {open ? "Hide supporting data" : "View supporting data"}
      </button>
      {open ? (
        <ul className="ws-list" data-testid="ws-brief-supporting" style={{ marginTop: 6 }}>
          {rows.map(([label, value]) => (
            <li key={label} className="ws-understanding-item">
              <span>{label}</span>
              <span className="ws-tag">{value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function AssessmentBody({ campaignId, assessment }) {
  if (!campaignId) {
    return (
      <EmptyState
        title="No campaign selected"
        detail="Select a flagged campaign to see what Cipher recommends for it."
      />
    );
  }
  return (
    <Async
      result={assessment}
      empty="Cipher has not assessed this campaign yet."
      deniedLabel="This requires a signed-in operator, and the server refused it. That is the gate working, not a fault."
    >
      {(data) => {
        // Four outcomes, four different things to say. `none` and
        // `unavailable` reading the same way is what lets a broken lookup
        // pass for an all-clear.
        if (data.state === "unavailable") {
          return (
            <div className="state ws-state" role="status">
              <strong>Heads up: </strong>
              Cipher could not load its assessment for this campaign. This is a
              failure to read it, not a sign that there is nothing to report.
            </div>
          );
        }
        if (data.state === "preparing") {
          return (
            <div className="state ws-state" role="status">
              Cipher has reached a conclusion on this campaign, but the
              plain-English summary is still being prepared. Check back shortly.
            </div>
          );
        }
        if (data.state === "none" || !data.assessment) {
          return (
            <EmptyState
              title="Not assessed yet"
              detail="Campaigns are flagged automatically each morning. A full assessment is a separate, deliberate review."
            />
          );
        }

        const a = data.assessment;
        return (
          <div data-testid="ws-brief">
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <span
                className="ws-tag"
                data-testid="ws-brief-decision"
                style={{ fontWeight: 700, letterSpacing: ".04em" }}
              >
                {a.decision_label}
              </span>
              <strong data-testid="ws-brief-campaign">{a.campaign_name}</strong>
            </div>

            <p className="ws-understanding-lead" style={{ marginTop: 0 }}>
              {a.assessment}
            </p>

            <h4 className="ws-subhead">Recommended action</h4>
            <p data-testid="ws-brief-action" style={{ marginTop: 0 }}>
              {a.recommended_action}
            </p>

            {Array.isArray(a.supporting_signals) && a.supporting_signals.length > 0 ? (
              <>
                <h4 className="ws-subhead">Why Cipher recommends it</h4>
                <ul className="ws-list" style={{ marginTop: 0 }}>
                  {a.supporting_signals.map((sig, i) => (
                    <li key={i} className="ws-understanding-item">
                      <span>{sig}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {a.conflict ? (
              <>
                <h4 className="ws-subhead">Conflicting signals</h4>
                <p data-testid="ws-brief-conflict" style={{ marginTop: 0 }}>
                  {a.conflict}
                </p>
              </>
            ) : null}

            <h4 className="ws-subhead">Risk</h4>
            <p data-testid="ws-brief-risk" style={{ marginTop: 0 }}>{a.risk}</p>

            <div
              style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}
            >
              <ConfidenceChip value={a.confidence} />
              {when(a.assessed_at) ? (
                <span className="dim" style={{ fontSize: "var(--f-label, 10.5px)" }}>
                  Last assessed {when(a.assessed_at)}
                </span>
              ) : null}
            </div>

            <SupportingData data={a.supporting_data} />
          </div>
        );
      }}
    </Async>
  );
}

export default function Opportunities({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;
  const [diagnosisCampaignId, setDiagnosisCampaignId] = useState(null);

  const atRisk = useEndpoint(
    () => api.getAtRisk(selectedId),
    [selectedId],
    { skip: !selectedId }
  );
  const scaleCandidates = useEndpoint(
    () => api.getScaleCandidates(selectedId),
    [selectedId],
    { skip: !selectedId }
  );
  const diagnosis = useEndpoint(
    () => api.getCampaignDiagnosis(diagnosisCampaignId),
    [diagnosisCampaignId],
    { skip: !diagnosisCampaignId }
  );
  const assessment = useEndpoint(
    () => api.getCampaignAssessment(diagnosisCampaignId),
    [diagnosisCampaignId],
    { skip: !diagnosisCampaignId }
  );

  // Either list carries the same sweep record; the scale-candidate response is
  // read first purely because it is fetched first, and the at-risk one stands
  // in when that request has not resolved.
  const sweep =
    scaleCandidates.data?.sweep ?? atRisk.data?.sweep ?? undefined;

  // Reset the diagnosis selection when the offer changes, so a stale
  // diagnosis from the previous offer's campaign never lingers on screen.
  React.useEffect(() => {
    setDiagnosisCampaignId(null);
  }, [selectedId]);

  return (
    <>
      <PageHead
        title="Opportunities"
        subtitle={
          selected
            ? `Scale candidates and at-risk campaigns for ${selected.name || "this offer"}.`
            : "Scale candidates and at-risk campaigns Cipher's health analysis has flagged."
        }
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see its flagged opportunities.
        </div>
      ) : (
        <>
          <SweepNote sweep={sweep} />
          <div className="ws-analytics-grid">
          <Panel
            title="Flagged: possible scale"
            subtitle="Caught by an automatic check — cost per result and click rate held or improved, with real conversions behind it. A flag, not a recommendation."
          >
            <FlaggedList
              result={scaleCandidates}
              label="as a Scale Candidate"
              metricsFn={scaleMetrics}
              selectedId={diagnosisCampaignId}
              onSelect={setDiagnosisCampaignId}
            />
          </Panel>

          <Panel
            title="Flagged: possible risk"
            subtitle="Caught by an automatic check — cost per result, cost per click and cost per thousand views all rising. A flag, not a recommendation."
          >
            <FlaggedList
              result={atRisk}
              label="At Risk"
              metricsFn={atRiskMetrics}
              selectedId={diagnosisCampaignId}
              onSelect={setDiagnosisCampaignId}
            />
          </Panel>

          <Panel
            title="Diagnosis"
            subtitle="Failure-mode diagnosis for the selected flagged campaign."
          >
            <DiagnosisBody campaignId={diagnosisCampaignId} diagnosis={diagnosis} />
          </Panel>

          <Panel
            title="What Cipher recommends"
            subtitle="Cipher's assessment of the selected campaign. Advisory — nothing here changes a budget or a campaign on its own."
          >
            <AssessmentBody
              campaignId={diagnosisCampaignId}
              assessment={assessment}
            />
          </Panel>
          </div>
        </>
      )}
    </>
  );
}
